
const libPictProvider = require('pict-provider');

const libCatalog = require('../access/Permission-Catalog.js');

/**
 * PermissionVisualizer — the Effective tab of the Access workspace.
 *
 * Shows the RESOLVED effective permissions for the selected subject, with provenance, grouped by CONTEXT:
 *   - User subject: every context the user holds a role in (Global + each project / contract / lab) as an
 *     expandable section, each listing every explicitly granted / denied permission with the implying
 *     role/membership. Any row drills to the full Explain ("why?") trace. This is the direct answer to
 *     "everything this user can/can't do, across their projects, and why".
 *   - Role/Group subject: the role's effective set in the workspace context, each grant tagged Direct vs
 *     Inherited (from an included permission group), as a single section.
 *
 * Read-only. It does NOT call the Permissions/* endpoints itself — it depends only on the
 * EffectivePermissionsService fable service (the swap seam), so a future server aggregation endpoint can
 * replace the service internals without touching this provider or the view. Subject + context come from the
 * AccessWorkspace provider; this owns AppData.AccessWorkspace.Effective.
 */
class PermissionVisualizerProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get workspace()
	{
		return this.pict.providers.AccessWorkspace;
	}

	get service()
	{
		return this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
	}

	/**
	 * The Effective slice of the shared workspace AppData (lazily created).
	 * @return {Object}
	 */
	get visualizer()
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.Effective)
		{
			tmpWorkspaceState.Effective =
			{
				SubjectKind: '', SubjectID: 0, Context: '', IDContext: 0, Loading: false, Loaded: false, Error: '', Search: '',
				Matrix: { Contexts: [], Permissions: {}, Summary: null }, Explain: null,
				Sections: [], SummarySlot: [], LoadingSlot: [], ErrorSlot: [], NoMatchSlot: [], ExplainSlot: [],
			};
		}
		return tmpWorkspaceState.Effective;
	}

	// --- Load (via the service) ---

	/**
	 * Resolve the effective matrix for the workspace's current subject. For a user this is every context they
	 * belong to; for a role/group it is the workspace context. Skips the fetch when nothing relevant changed.
	 * @param {Boolean} [pForce]
	 */
	async loadAsync(pForce)
	{
		const tmpWorkspaceState = this.workspace.state;
		if (!tmpWorkspaceState.SubjectRecord)
		{
			return;
		}
		const tmpKind = tmpWorkspaceState.SubjectKind;
		const tmpID = Number(tmpWorkspaceState.SubjectID);
		const tmpContext = tmpWorkspaceState.Context;
		const tmpIDContext = Number(tmpWorkspaceState.IDContext);
		const tmpVisualizer = this.visualizer;

		// A user's effective view spans ALL contexts, so it is independent of the workspace context bar; a
		// role's is scoped to the chosen context.
		const tmpUnchanged = tmpVisualizer.Loaded && !pForce &&
			(tmpVisualizer.SubjectKind === tmpKind) && (tmpVisualizer.SubjectID === tmpID) &&
			((tmpKind === 'user') || ((tmpVisualizer.Context === tmpContext) && (tmpVisualizer.IDContext === tmpIDContext)));
		if (tmpUnchanged)
		{
			this._render();
			return;
		}

		tmpVisualizer.SubjectKind = tmpKind;
		tmpVisualizer.SubjectID = tmpID;
		tmpVisualizer.Context = tmpContext;
		tmpVisualizer.IDContext = tmpIDContext;
		tmpVisualizer.Loaded = false;
		tmpVisualizer.Error = '';
		tmpVisualizer.Explain = null;
		tmpVisualizer.Matrix = { Contexts: [], Permissions: {}, Summary: null };

		if (tmpKind !== 'user' && !tmpIDContext)
		{
			tmpVisualizer.Loading = false;
			tmpVisualizer.Error = `Enter a ${tmpContext} ID above to resolve permissions.`;
			this._render();
			return;
		}
		if (!this.service)
		{
			tmpVisualizer.Loading = false;
			tmpVisualizer.Error = 'The effective-permissions service is not available.';
			this._render();
			return;
		}

		tmpVisualizer.Loading = true;
		this._render();
		try
		{
			const tmpResult = (tmpKind === 'user')
				? await this.service.resolveUserMatrixAsync(tmpID, {})
				: await this.service.resolveRoleMatrixAsync(tmpID, tmpContext, tmpIDContext, tmpWorkspaceState.SubjectRecord);
			tmpVisualizer.Matrix = { Contexts: tmpResult.Contexts || [], Permissions: tmpResult.Permissions || {}, Summary: tmpResult.Summary || null };
			tmpVisualizer.Loaded = true;
		}
		catch (pError)
		{
			tmpVisualizer.Error = `Could not resolve permissions: ${pError && pError.message ? pError.message : pError}`;
		}
		tmpVisualizer.Loading = false;
		this._render();
	}

	setSearch(pValue)
	{
		this.visualizer.Search = pValue || '';
		this._render();
	}

	/**
	 * Explain a single permission for the current user subject in a specific context (the "why?" drilldown).
	 * @param {String} pHash
	 * @param {String} pContext
	 * @param {Number} pIDContext
	 */
	async explainAsync(pHash, pContext, pIDContext)
	{
		const tmpVisualizer = this.visualizer;
		if (tmpVisualizer.SubjectKind !== 'user' || !this.service)
		{
			return;
		}
		tmpVisualizer.Explain = { Hash: pHash, Context: pContext, IDContext: pIDContext, Loading: true };
		this._render();
		try
		{
			const tmpResult = await this.service.explainAsync(tmpVisualizer.SubjectID, pHash, pContext, pIDContext);
			tmpVisualizer.Explain = { Hash: pHash, Loading: false, Result: tmpResult };
		}
		catch (pError)
		{
			tmpVisualizer.Explain = { Hash: pHash, Loading: false, Error: `Could not explain: ${pError && pError.message ? pError.message : pError}` };
		}
		this._render();
	}

	closeExplain()
	{
		this.visualizer.Explain = null;
		this._render();
	}

	// --- Derive (pure; called from the workspace render cycle) ---

	/**
	 * Recompute the visualizer render model: one section per context, each a filtered list of resolved
	 * permissions with provenance. No network.
	 */
	deriveVisualizer()
	{
		const tmpVisualizer = this.visualizer;
		tmpVisualizer.LoadingSlot = tmpVisualizer.Loading ? [ {} ] : [];
		tmpVisualizer.ErrorSlot = tmpVisualizer.Error ? [ { Error: tmpVisualizer.Error } ] : [];
		tmpVisualizer.ExplainSlot = this._deriveExplainSlot(tmpVisualizer);

		if (tmpVisualizer.Loading || tmpVisualizer.Error)
		{
			tmpVisualizer.Sections = [];
			tmpVisualizer.NoMatchSlot = [];
			tmpVisualizer.SummarySlot = [];
			return;
		}

		const tmpMatrix = tmpVisualizer.Matrix || { Contexts: [], Permissions: {} };
		const tmpSearch = (tmpVisualizer.Search || '').toLowerCase().trim();
		const tmpAllSections = (tmpMatrix.Contexts || []).map((pCtx) => this._buildSection(pCtx, tmpMatrix.Permissions, tmpSearch, tmpVisualizer.SubjectKind));
		const tmpVisible = tmpAllSections.filter((pSection) => (pSection.Rows.length > 0) || !tmpSearch);
		tmpVisualizer.Sections = tmpVisible;
		tmpVisualizer.NoMatchSlot = tmpVisible.length ? [] : [ {} ];

		let tmpAllowed = 0;
		let tmpDenied = 0;
		tmpAllSections.forEach((pSection) => { tmpAllowed += pSection.Allowed; tmpDenied += pSection.Denied; });
		if (tmpVisualizer.SubjectKind === 'user')
		{
			tmpVisualizer.SummarySlot = [ { Text: `${(tmpMatrix.Contexts || []).length} context(s) · ${tmpAllowed} granted · ${tmpDenied} denied` } ];
		}
		else if (tmpMatrix.Summary)
		{
			tmpVisualizer.SummarySlot = [ { Text: `${tmpMatrix.Summary.Total || 0} effective (${tmpMatrix.Summary.Direct || 0} direct · ${tmpMatrix.Summary.Inherited || 0} inherited)` } ];
		}
		else
		{
			tmpVisualizer.SummarySlot = [];
		}
	}

	/**
	 * Build a context section: its header (label + membership badges + counts) + the filtered permission rows.
	 * @return {Object}
	 */
	_buildSection(pCtx, pPermissions, pSearch, pSubjectKind)
	{
		const tmpKey = `${pCtx.Context}:${pCtx.IDContext}`;
		const tmpPerms = (pPermissions && pPermissions[tmpKey]) || {};
		const tmpHashes = Object.keys(tmpPerms).sort();
		const tmpRows = [];
		let tmpAllowed = 0;
		let tmpDenied = 0;
		tmpHashes.forEach((pHash) =>
		{
			const tmpEntry = tmpPerms[pHash];
			if (tmpEntry.Allowed) { tmpAllowed++; } else { tmpDenied++; }
			if (pSearch && pHash.toLowerCase().indexOf(pSearch) < 0)
			{
				return;
			}
			tmpRows.push(this._permissionRow(pHash, tmpEntry, pCtx, pSubjectKind));
		});
		const tmpMembers = (pCtx.MembershipVia || []).map((pMember) => ({ Label: pMember.RoleName, IDRole: pMember.IDRole, Type: pMember.MembershipType }));
		return {
			Key: tmpKey,
			Label: pCtx.Label,
			Context: pCtx.Context,
			IDContext: pCtx.IDContext,
			Allowed: tmpAllowed,
			Denied: tmpDenied,
			Total: tmpHashes.length,
			MemberSlot: tmpMembers.length ? [ { Members: tmpMembers } ] : [],
			SuperSlot: pCtx.IsSuperUser ? [ {} ] : [],
			Rows: tmpRows,
			EmptySlot: tmpRows.length ? [] : [ {} ],
		};
	}

	/**
	 * A single resolved-permission row: entity·action, allow/deny status, the implying source, and (user
	 * subjects) a "why?" drill.
	 * @return {Object}
	 */
	_permissionRow(pHash, pEntry, pCtx, pSubjectKind)
	{
		const tmpParsed = libCatalog.parseHash(pHash);
		const tmpAllowed = !!pEntry.Allowed;
		const tmpSource = pEntry.Source || 'Default';
		let tmpSourceLabel = tmpSource;
		let tmpSourceClass = 'is-light';
		if (tmpSource === 'RolePermission' || tmpSource === 'Role')
		{
			tmpSourceLabel = pEntry.RoleName ? `role: ${pEntry.RoleName}` : 'role';
			tmpSourceClass = 'is-info is-light';
		}
		else if (tmpSource === 'UserOverride' || tmpSource === 'Override')
		{
			tmpSourceLabel = 'user override';
			tmpSourceClass = 'is-warning is-light';
		}
		else if (tmpSource === 'Inherited')
		{
			tmpSourceLabel = 'inherited';
			tmpSourceClass = 'is-link is-light';
		}
		else if (tmpSource === 'Direct')
		{
			tmpSourceLabel = 'direct';
			tmpSourceClass = 'is-info is-light';
		}
		else if (tmpSource === 'Default')
		{
			tmpSourceLabel = tmpAllowed ? 'default (super-user)' : 'default';
			tmpSourceClass = 'is-light';
		}
		return {
			Hash: pHash,
			Entity: tmpParsed.Entity,
			Action: tmpParsed.Action,
			Context: pCtx.Context,
			IDContext: pCtx.IDContext,
			RowClass: tmpAllowed ? 'acw-eff-allow' : 'acw-eff-deny',
			StatusIcon: tmpAllowed ? '<i class="fas fa-circle-check has-text-success"></i>' : '<i class="fas fa-ban has-text-danger"></i>',
			SourceLabel: tmpSourceLabel,
			SourceClass: tmpSourceClass,
			WhySlot: (pSubjectKind === 'user') ? [ { Hash: pHash, Context: pCtx.Context, IDContext: pCtx.IDContext } ] : [],
		};
	}

	_deriveExplainSlot(pVisualizer)
	{
		const tmpExplain = pVisualizer.Explain;
		if (!tmpExplain)
		{
			return [];
		}
		return [
			{
				Hash: tmpExplain.Hash,
				LoadingSlot: tmpExplain.Loading ? [ {} ] : [],
				ErrorSlot: tmpExplain.Error ? [ { Error: tmpExplain.Error } ] : [],
				Lines: (!tmpExplain.Loading && !tmpExplain.Error) ? this._explainLines(tmpExplain.Result) : [],
			},
		];
	}

	/**
	 * Flatten an Explain result into readable trace lines.
	 * @param {Object} pResult
	 * @return {Array<Object>}
	 */
	_explainLines(pResult)
	{
		const tmpLines = [];
		if (!pResult)
		{
			return tmpLines;
		}
		const tmpDecided = pResult.DecidedBy || {};
		tmpLines.push({ Class: pResult.Allowed ? 'has-text-success' : 'has-text-danger', Text: `${pResult.Allowed ? 'ALLOWED' : 'DENIED'} — decided by ${tmpDecided.Step || '(unknown)'}${tmpDecided.RoleName ? ` (${tmpDecided.RoleName})` : ''}` });
		(pResult.Trace || []).forEach((pStep) =>
		{
			const tmpCount = (pStep.Records || []).length;
			let tmpDetail = pStep.Description || pStep.Step;
			if (pStep.Step === 'RolePermission' && pStep.Roles)
			{
				tmpDetail += ` — roles: ${(pStep.Roles).map((pRole) => pRole.Name).join(', ') || 'none'}`;
			}
			if (pStep.Step === 'Default')
			{
				tmpDetail += ` — ${pStep.IsSuperUser ? 'super-user' : 'not a super-user'}`;
			}
			tmpLines.push({ Class: 'has-text-grey', Text: `${pStep.Step}: ${tmpDetail}${tmpCount ? ` (${tmpCount} record${tmpCount === 1 ? '' : 's'})` : ''}` });
		});
		return tmpLines;
	}

	// --- Navigation helper ---

	selectRole(pID)
	{
		if (Number(pID) > 0)
		{
			this.workspace.selectRoleAsync(pID);
		}
	}

	_render()
	{
		if (this.pict.views.AccessWorkspace)
		{
			this.pict.views.AccessWorkspace.render();
		}
	}
}

module.exports = PermissionVisualizerProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'PermissionVisualizer',
	AutoInitialize: true,
	AutoInitializeOrdinal: 2,
};
